import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { CheckCircle, XCircle, Eye, ZoomIn, Download } from 'lucide-react';

// Receipt Viewer Component
const ReceiptViewer = ({ receiptPath }: { receiptPath: string }) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadReceipt = async () => {
      try {
        const { data, error } = await supabase.storage
          .from('payment-receipts')
          .createSignedUrl(receiptPath, 3600);

        if (!error && data) {
          setSignedUrl(data.signedUrl);
        }
      } catch (err) {
        console.error('Error loading receipt:', err);
      } finally {
        setLoading(false);
      }
    };

    loadReceipt();
  }, [receiptPath]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading receipt...</div>
      </div>
    );
  }

  if (!signedUrl) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Receipt not found</div>
      </div>
    );
  }

  // Check if file is PDF
  const isPdf = receiptPath.toLowerCase().endsWith('.pdf') || signedUrl.toLowerCase().includes('.pdf');

  return (
    <div className="relative w-full">
      {isPdf ? (
        <div className="w-full">
          <iframe
            src={signedUrl}
            className="w-full h-[600px] border border-border rounded-md"
            title="Payment receipt PDF"
          />
          <div className="mt-3 text-center">
            <button
              onClick={() => {
                window.open(signedUrl, '_blank');
              }}
              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 mx-auto px-3 py-1.5 border border-primary/20 rounded-md hover:bg-primary/5"
            >
              <ZoomIn size={14} />
              Open PDF in new tab
            </button>
          </div>
        </div>
      ) : (
        <div>
          <img
            src={signedUrl}
            alt="Payment receipt"
            className="w-full h-auto max-h-[600px] object-contain mx-auto rounded-md shadow-sm"
          />
          <div className="mt-3 text-center">
            <button
              onClick={() => {
                window.open(signedUrl, '_blank');
              }}
              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 mx-auto px-3 py-1.5 border border-primary/20 rounded-md hover:bg-primary/5"
            >
              <ZoomIn size={14} />
              Open in new tab for full size
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface PaymentRecord {
  id: string;
  payment_reference: string;
  amount_vatu: number;
  payment_method: string;
  payment_proof_url: string | null;
  payment_status: 'pending' | 'approved' | 'rejected';
  submitted_at: string;
  rejection_reason: string | null;
  approved_at: string | null;
  user: {
    id: string;
    full_name: string;
    email: string;
  };
  subscription?: {
    id: string;
    plan?: {
      plan_name: string;
      price_vatu: number;
    };
    group?: {
      id: string;
      name: string;
    };
  } | null;
}

const PaymentApprovalDashboard = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState<PaymentRecord | null>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptViewed, setReceiptViewed] = useState<Set<string>>(new Set());
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');

  useEffect(() => {
    fetchPayments();
  }, [filter]);

  const fetchPayments = async () => {
    try {
      setLoading(true);

      // Query payment transactions with related data
      let query = supabase
        .from('payment_transactions')
        .select(`
          id,
          payment_reference,
          amount_vatu,
          payment_method,
          payment_proof_url,
          payment_status,
          submitted_at,
          rejection_reason,
          approved_at,
          user_id,
          subscription_id,
          user:user_profiles!fk_payment_transactions_user (
            id,
            full_name,
            email
          ),
          subscription:user_subscriptions (
            id,
            plan_id,
            plan:subscription_plans (
              plan_name,
              price_vatu
            )
          )
        `)
        .order('submitted_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('payment_status', filter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching payments:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      console.log('Fetched payments:', {
        count: data?.length || 0,
        filter,
        payments: data
      });

      // Enhance payment data: if plan is missing but plan_id exists, fetch plan separately
      const enhancedPayments = await Promise.all(
        (data || []).map(async (payment: any) => {
          // If subscription exists but plan is missing, try to fetch plan using plan_id
          if (payment.subscription && !payment.subscription.plan && payment.subscription.plan_id) {
            try {
              const { data: planData } = await supabase
                .from('subscription_plans')
                .select('plan_name, price_vatu')
                .eq('id', payment.subscription.plan_id)
                .maybeSingle();
              
              if (planData) {
                payment.subscription.plan = planData;
              }
            } catch (e) {
              console.error('Error fetching plan for payment:', e);
            }
          }
          
          // If subscription is null but subscription_id exists, try to fetch subscription and plan
          if (!payment.subscription && payment.subscription_id) {
            try {
              const { data: subData } = await supabase
                .from('user_subscriptions')
                .select(`
                  id,
                  plan_id,
                  plan:subscription_plans (
                    plan_name,
                    price_vatu
                  )
                `)
                .eq('id', payment.subscription_id)
                .maybeSingle();
              
              if (subData) {
                payment.subscription = subData;
                
                // If plan is still missing but plan_id exists, fetch plan directly
                if (!subData.plan && subData.plan_id) {
                  const { data: planData } = await supabase
                    .from('subscription_plans')
                    .select('plan_name, price_vatu')
                    .eq('id', subData.plan_id)
                    .maybeSingle();
                  
                  if (planData) {
                    payment.subscription.plan = planData;
                  }
                }
              }
            } catch (e) {
              console.error('Error fetching subscription for payment:', e);
            }
          }
          
          return payment;
        })
      );

      setPayments(enhancedPayments as PaymentRecord[]);
    } catch (error) {
      console.error('Error fetching payments:', error);
      // Show error to user
      alert(`Failed to load payments: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (paymentId: string) => {
    if (!user) return;

    // Find the payment to check if it has a receipt
    const payment = payments.find(p => p.id === paymentId);
    
    // If payment has a receipt but hasn't been viewed, require viewing first
    if (payment?.payment_proof_url && !receiptViewed.has(paymentId)) {
      setSelectedPayment(payment);
      setShowReceiptModal(true);
      alert('Please review the payment receipt before approving. The receipt will be shown in the modal.');
      return;
    }

    try {
      setProcessing(true);

      // Get payment details with all necessary data
      const { data: paymentData, error: paymentError } = await supabase
        .from('payment_transactions')
        .select(`
          *,
          user_id,
          subscription_id,
          amount_vatu,
          subscription:user_subscriptions (
            id,
            plan:subscription_plans (
              id,
              plan_name,
              price_vatu
            )
          )
        `)
        .eq('id', paymentId)
        .single();

      if (paymentError) throw paymentError;
      if (!paymentData) throw new Error('Payment not found');

      // Update payment status
      const { error: updateError } = await supabase
        .from('payment_transactions')
        .update({
          payment_status: 'approved',
          approved_by: user.id,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', paymentId);

      if (updateError) throw updateError;

      // Get or create subscription
      let subscriptionId = paymentData.subscription_id;
      const subscription = paymentData.subscription as any;
      const planId = subscription?.plan?.id;

      if (!subscriptionId) {
        // Find existing subscription for this user
        const { data: existingSub, error: subFindError } = await supabase
          .from('user_subscriptions')
          .select('id, plan_id, end_date')
          .eq('user_id', paymentData.user_id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (subFindError && subFindError.code !== 'PGRST116') {
          throw subFindError;
        }

        if (existingSub) {
          subscriptionId = existingSub.id;
        } else if (planId) {
          // Create new subscription if it doesn't exist
          const endDate = new Date();
          endDate.setDate(endDate.getDate() + 30); // 30 days

          const { data: newSub, error: createSubError } = await supabase
            .from('user_subscriptions')
            .insert({
              user_id: paymentData.user_id,
              plan_id: planId,
              subscription_status: 'active',
              access_locked: false,
              start_date: new Date().toISOString(),
              end_date: endDate.toISOString(),
            })
            .select('id')
            .single();

          if (createSubError) throw createSubError;
          subscriptionId = newSub.id;
        }
      }

      // Update payment transaction with subscription_id if it was missing
      if (!paymentData.subscription_id && subscriptionId) {
        await supabase
          .from('payment_transactions')
          .update({ subscription_id: subscriptionId })
          .eq('id', paymentId);
      }

      // Extend subscription if we have one
      if (subscriptionId) {
        const { data: currentSub, error: subError } = await supabase
          .from('user_subscriptions')
          .select(`
            end_date, 
            subscription_status,
            plan:subscription_plans(billing_model, plan_type)
          `)
          .eq('id', subscriptionId)
          .single();

        if (subError) {
          console.warn('Could not fetch subscription for extension:', subError);
        } else {
          const plan = currentSub.plan as any;
          const isPayAsYouGo = plan?.billing_model === 'per_member';
          
          let newEndDate: Date;
          let newPaymentDueDate: Date;

          if (isPayAsYouGo) {
            // For pay-as-you-go: payment_due_date is end of NEXT month
            const now = new Date();
            const nextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0, 23, 59, 59);
            newPaymentDueDate = nextMonth;
            
            // For pay-as-you-go, end_date can be extended but payment_due_date is monthly
            const currentEndDate = currentSub.end_date 
              ? new Date(currentSub.end_date) 
              : new Date();
            newEndDate = new Date(currentEndDate);
            newEndDate.setDate(newEndDate.getDate() + 30); // Extend by 30 days
          } else {
            // For flat-rate plans: extend by 30 days, payment_due_date matches end_date
            const currentEndDate = currentSub.end_date 
              ? new Date(currentSub.end_date) 
              : new Date();
            newEndDate = new Date(currentEndDate);
            newEndDate.setDate(newEndDate.getDate() + 30); // Extend by 30 days
            newPaymentDueDate = newEndDate;
          }

          const { error: extendError } = await supabase
            .from('user_subscriptions')
            .update({
              end_date: newEndDate.toISOString(),
              payment_due_date: newPaymentDueDate.toISOString(),
              subscription_status: 'active',
              access_locked: false,
              updated_at: new Date().toISOString()
            })
            .eq('id', subscriptionId);

          if (extendError) {
            console.warn('Could not extend subscription:', extendError);
          }
        }
      }

      // Get user info for notification
      const { data: userData } = await supabase
        .from('user_profiles')
        .select('id, full_name')
        .eq('id', paymentData.user_id)
        .single();

      // Create notification for group admin
      await supabase
        .from('notifications')
        .insert({
          user_id: paymentData.user_id,
          type: 'payment_approved',
          title: 'Payment Approved',
          message: `Your payment of ${Number(paymentData.amount_vatu).toLocaleString()} VT has been approved. Subscription extended.`,
          related_entity_id: paymentId,
          is_read: false,
        });

      await fetchPayments();
      setSelectedPayment(null);

      // Generate & email invoice to the payer (group admin) after approval.
      // Note: This requires the Edge Function `send-payment-invoice` to be deployed.
      try {
        const { data, error } = await supabase.functions.invoke('send-payment-invoice', {
          body: { paymentId },
        });
        if (error) throw error;
        if (!data?.ok) {
          console.warn('Invoice generation/email returned non-ok:', data);
        }
      } catch (invoiceErr) {
        console.error('Invoice generation/email failed:', invoiceErr);
        // Don't block approval flow; just notify the admin.
        alert('Payment approved, but invoice email failed. Please check Edge Function logs.');
      }

    } catch (error: any) {
      console.error('Error approving payment:', error);
      alert(`Failed to approve payment: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (paymentId: string) => {
    if (!user || !rejectionReason.trim()) return;

    try {
      setProcessing(true);

      // Get payment details
      const { data: paymentData, error: paymentError } = await supabase
        .from('payment_transactions')
        .select('user_id, amount_vatu')
        .eq('id', paymentId)
        .single();

      if (paymentError) throw paymentError;
      if (!paymentData) throw new Error('Payment not found');

      const { error } = await supabase
        .from('payment_transactions')
        .update({
          payment_status: 'rejected',
          approved_by: user.id,
          rejection_reason: rejectionReason,
          updated_at: new Date().toISOString()
        })
        .eq('id', paymentId);

      if (error) throw error;

      // Create notification for group admin
      await supabase
        .from('notifications')
        .insert({
          user_id: paymentData.user_id,
          type: 'payment_rejected',
          title: 'Payment Rejected',
          message: `Your payment was rejected. Reason: ${rejectionReason}`,
          related_entity_id: paymentId,
          is_read: false,
        });

      await fetchPayments();
      setShowRejectModal(false);
      setSelectedPayment(null);
      setRejectionReason('');

    } catch (error: any) {
      console.error('Error rejecting payment:', error);
      alert(`Failed to reject payment: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const stats = {
    pending: payments.filter(p => p.payment_status === 'pending').length,
    approved: payments.filter(p => p.payment_status === 'approved').length,
    rejected: payments.filter(p => p.payment_status === 'rejected').length,
    total: payments.length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading payments...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Pending</p>
              <p className="text-2xl font-bold text-warning">{stats.pending}</p>
            </div>
            <Icon name="Clock" size={24} className="text-warning opacity-50" />
          </div>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Approved</p>
              <p className="text-2xl font-bold text-success">{stats.approved}</p>
            </div>
            <Icon name="CheckCircle" size={24} className="text-success opacity-50" />
          </div>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Rejected</p>
              <p className="text-2xl font-bold text-destructive">{stats.rejected}</p>
            </div>
            <Icon name="XCircle" size={24} className="text-destructive opacity-50" />
          </div>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total</p>
              <p className="text-2xl font-bold text-foreground">{stats.total}</p>
            </div>
            <Icon name="DollarSign" size={24} className="text-primary opacity-50" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {(['all', 'pending', 'approved', 'rejected'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Payments List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-card border border-border rounded-md p-12 text-center">
            <div className="text-muted-foreground">Loading payments...</div>
          </div>
        ) : payments.length === 0 ? (
          <div className="bg-card border border-border rounded-md p-12 text-center">
            <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground mb-2">No {filter === 'all' ? '' : filter} payments found</p>
            <p className="text-xs text-muted-foreground">
              {filter === 'pending' && 'Pending payments will appear here once users submit payment requests.'}
              {filter === 'approved' && 'Approved payments will appear here.'}
              {filter === 'rejected' && 'Rejected payments will appear here.'}
              {filter === 'all' && 'No payments found in the system.'}
            </p>
          </div>
        ) : (
          payments.map((payment) => (
            <div
              key={payment.id}
              className="bg-card border border-border rounded-md p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      payment.payment_status === 'pending'
                        ? 'bg-warning/10 text-warning'
                        : payment.payment_status === 'approved'
                        ? 'bg-success/10 text-success'
                        : 'bg-destructive/10 text-destructive'
                    }`}>
                      {payment.payment_status.toUpperCase()}
                    </div>
                    <span className="text-sm font-mono text-muted-foreground">
                      {payment.payment_reference}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">User</p>
                      <p className="text-sm font-medium text-foreground">
                        {(payment.user as any)?.full_name || 'Unknown'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Amount</p>
                      <p className="text-sm font-medium text-foreground">
                        {payment.amount_vatu.toLocaleString()} VT
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Submitted</p>
                      <p className="text-sm font-medium text-foreground">
                        {new Date(payment.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Method</p>
                      <p className="text-sm font-medium text-foreground capitalize">
                        {payment.payment_method?.replace('_', ' ') || 'Bank Transfer'}
                      </p>
                    </div>
                  </div>

                  {payment.rejection_reason && (
                    <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                      <p className="text-xs text-destructive font-medium mb-1">Rejection Reason:</p>
                      <p className="text-sm text-destructive">{payment.rejection_reason}</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {/* View Details/Receipt Button - Always visible */}
                  <Button
                    onClick={() => {
                      setSelectedPayment(payment);
                      setShowReceiptModal(true);
                      // Mark receipt as viewed when modal opens
                      if (payment.payment_proof_url) {
                        setReceiptViewed(prev => new Set(prev).add(payment.id));
                      }
                    }}
                    variant="outline"
                    size="sm"
                    iconName={payment.payment_proof_url ? "Eye" : "FileText"}
                    className={payment.payment_proof_url && !receiptViewed.has(payment.id) ? 'ring-2 ring-amber-500' : ''}
                    title={payment.payment_proof_url 
                      ? (!receiptViewed.has(payment.id) ? "Review receipt document before approving" : "View payment details and receipt")
                      : "View payment details"}
                  >
                    {payment.payment_proof_url ? (receiptViewed.has(payment.id) ? 'View Receipt' : 'Review Receipt') : 'View Details'}
                  </Button>
                  
                  {payment.payment_status === 'pending' && (
                    <>
                      {payment.payment_proof_url && !receiptViewed.has(payment.id) && (
                        <span className="text-xs text-amber-600 font-medium px-2 py-1 bg-amber-50 dark:bg-amber-900/20 rounded" title="Please review receipt before approving">
                          ⚠️ Review Required
                        </span>
                      )}
                      <Button
                        onClick={() => {
                          if (payment.payment_proof_url && !receiptViewed.has(payment.id)) {
                            setSelectedPayment(payment);
                            setShowReceiptModal(true);
                          } else {
                            handleApprove(payment.id);
                          }
                        }}
                        variant="default"
                        size="sm"
                        disabled={processing || (payment.payment_proof_url && !receiptViewed.has(payment.id))}
                        iconName="CheckCircle"
                        title={payment.payment_proof_url && !receiptViewed.has(payment.id) ? "Please review receipt first" : "Approve payment"}
                      >
                        Approve
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedPayment(payment);
                          setShowRejectModal(true);
                        }}
                        variant="outline"
                        size="sm"
                        disabled={processing}
                        iconName="XCircle"
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Payment Details</h2>
              <button
                onClick={() => {
                  setShowReceiptModal(false);
                  setSelectedPayment(null);
                }}
                className="p-2 hover:bg-muted rounded-md"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Payment Reference</p>
                  <p className="text-sm font-mono text-foreground">{selectedPayment.payment_reference}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Amount</p>
                  <p className="text-sm font-semibold text-foreground">
                    {selectedPayment.amount_vatu.toLocaleString()} VT
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Plan</p>
                  <p className="text-sm text-foreground">
                    {(selectedPayment.subscription as any)?.plan?.plan_name || 
                     ((selectedPayment.subscription as any)?.plan_id ? `Plan ID: ${(selectedPayment.subscription as any).plan_id}` : 'No Plan Assigned')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Submitted By</p>
                  <p className="text-sm text-foreground">
                    {(selectedPayment.user as any)?.full_name || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Email</p>
                  <p className="text-sm text-foreground">
                    {(selectedPayment.user as any)?.email || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Submitted Date</p>
                  <p className="text-sm text-foreground">
                    {new Date(selectedPayment.submitted_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {selectedPayment.payment_proof_url ? (
                <div className="border-t border-border pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-lg font-semibold text-foreground mb-1">Payment Receipt Document</p>
                      <p className="text-xs text-muted-foreground">Please review the uploaded receipt before approving this payment</p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const { data, error } = await supabase.storage
                            .from('payment-receipts')
                            .createSignedUrl(selectedPayment.payment_proof_url!, 3600);
                          if (!error && data) {
                            window.open(data.signedUrl, '_blank');
                          }
                        } catch (err) {
                          console.error('Error downloading receipt:', err);
                        }
                      }}
                      className="text-sm text-primary hover:text-primary/80 flex items-center gap-2 px-3 py-2 border border-primary/20 rounded-md hover:bg-primary/5"
                    >
                      <Download size={16} />
                      Download Receipt
                    </button>
                  </div>
                  <div className="border-2 border-border rounded-lg overflow-hidden bg-muted/30">
                    <div className="p-4 bg-muted/50 border-b border-border">
                      <p className="text-xs text-muted-foreground font-medium">Receipt Preview</p>
                    </div>
                    <div className="p-4 max-h-[600px] overflow-auto">
                      <ReceiptViewer receiptPath={selectedPayment.payment_proof_url} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-t border-border pt-6">
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                    <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-1">
                      ⚠️ No Receipt Uploaded
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      This payment does not have a receipt document. You may still approve or reject the payment.
                    </p>
                  </div>
                </div>
              )}

              {selectedPayment.payment_status === 'pending' && (
                <div className="flex flex-col gap-3 pt-4 border-t border-border">
                  {selectedPayment.payment_proof_url && (
                    <div className={`p-3 rounded-md ${
                      receiptViewed.has(selectedPayment.id)
                        ? 'bg-success/10 border border-success/20'
                        : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                    }`}>
                      <p className={`text-sm font-medium mb-1 ${
                        receiptViewed.has(selectedPayment.id)
                          ? 'text-success'
                          : 'text-amber-800 dark:text-amber-200'
                      }`}>
                        {receiptViewed.has(selectedPayment.id) ? '✓ Receipt Reviewed' : '⚠️ Please Review Receipt Above'}
                      </p>
                      <p className={`text-xs ${
                        receiptViewed.has(selectedPayment.id)
                          ? 'text-success/80'
                          : 'text-amber-700 dark:text-amber-300'
                      }`}>
                        {receiptViewed.has(selectedPayment.id)
                          ? 'You have reviewed the receipt. You can now approve this payment.'
                          : 'Please carefully review the receipt document above before approving this payment.'}
                      </p>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        // Mark as viewed if not already
                        if (selectedPayment.payment_proof_url) {
                          setReceiptViewed(prev => new Set(prev).add(selectedPayment.id));
                        }
                        handleApprove(selectedPayment.id);
                      }}
                      variant="default"
                      disabled={processing}
                      iconName="CheckCircle"
                      className="flex-1"
                    >
                      Approve Payment
                    </Button>
                    <Button
                      onClick={() => {
                        setShowReceiptModal(false);
                        setShowRejectModal(true);
                      }}
                      variant="outline"
                      disabled={processing}
                      iconName="XCircle"
                      className="flex-1"
                    >
                      Reject Payment
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-foreground">Reject Payment</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 rounded-md border border-input bg-card text-foreground resize-none"
                  placeholder="Please provide a reason for rejection..."
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('');
                  }}
                  variant="outline"
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleReject(selectedPayment.id)}
                  variant="default"
                  disabled={!rejectionReason.trim() || processing}
                  fullWidth
                >
                  Reject Payment
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentApprovalDashboard;

